// @flow

/**
 * Module dependencies.
 */

import { Col, Container, Row } from 'client/components/core/grid';
import { Link } from 'react-router-dom';
import { LinkDefault } from 'client/components/core/buttons';
import { List, ListItem } from 'client/components/core/lists';
import { branch } from 'core/utils';
import { breakpoints, colors, defaultTransition, media, states, type, units } from 'client/styles';
import { filter } from 'lodash';
import { getLocalizedUrl, isLocalizedUrlActive, setBodyOverflow } from 'client/core/utils';
import { position, size } from 'polished';
import { withRouter } from 'react-router';
import Languages from 'client/components/core/languages';
import Layout from 'client/components/core/layout';
import LoginButton from 'client/components/navbar/login-button';
import MediaQuery from 'react-responsive';
import React, { Component } from 'react';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  footerLinks: Array<Object>,
  isOpen: boolean,
  location: Object,
  loginLabel: string,
  menuLinks: Array<Object>
};

/**
 * `Panel` component.
 */

const Panel = styled.div`
  ${position('fixed', '0', '0', null, 0)};
  ${size('100vh', '100vw')}

  background-color: ${colors.shadesOfSand2.pampasDarker};
  padding: ${units(4)};
  transition: transform ${defaultTransition};

  ${({ active }) => `
    pointer-events: ${active ? 'all' : 'none'};
    transform: translateY(${active ? '0' : '-100%'});
  `}
`;

/**
 * `PanelContainer` component.
 */

const PanelContainer = styled.div`
  ${position('absolute', '0', '0', '0', '0')};
  ${size('100%', '100%')};

  align-items: center;
  display: flex;
  justify-content: center;
  transition: opacity ${defaultTransition};

  ${({ active }) => `
    opacity: ${active ? 1 : 0};
    transition-delay: ${active ? '.4s' : '0s'};
  `}
`;

/**
 * `Item` component.
 */

const Item = styled(ListItem)`
  display: block;
  float: none;
  margin: ${units(1.5)} 0;
`;

/**
 * `LinkItem` component.
 */

const LinkItem = styled(Link)`
  font-family: ${type.fontFamily.poppins};
  font-size: 36px;
  font-weight: 400;
  letter-spacing: -0.3px;
  line-height: 48px;

  color: ${colors.brandSecondary};
  transition: color ${defaultTransition};

  ${states.action`
    text-decoration: none;
  `};

  ${media.portrait.xxs`
    font-size: 27px;
    letter-spacing: 0;
    line-height: 35px;
  `};

  ${media.landscape.sm`
    font-size: 27px;
    letter-spacing: 0;
    line-height: 32px;
  `};

  ${states.interaction`
    color: ${colors.brandPrimary};
  `};
`;

/**
 * `FooterLinksInlineWrapper` component.
 */

const FooterLinksInlineWrapper = styled.div`
  ${position('absolute', null, '0', units(1), '0')};
  ${size('105px', '100%')};

  text-align: center;

  ${media.landscape.sm`
    bottom: -20px;
  `};
`;

/**
 * `FooterNav` component.
 */

const FooterNav = styled.div`
  ${position('absolute', null, '0', '0', '0')};
  ${size('65px', '100%')};

  background-color: ${colors.brandSecondary};
  transition: transform ${defaultTransition};

  ${({ active }): string => `
    transform: translateY(${active ? '0px' : '80px'});
    transition-delay: ${active ? '0.7s' : '0'};
  `}

  &:before {
    ${position('absolute', '0', null, '0', '50%')};
    ${size('100%', '1px')};

    background-color: ${colors.white};
    content: '';
    opacity: 0.2;
  }
`;

/**
 * `FooterNavLink` component.
 */

const FooterNavLink = styled.span`
  ${size('65px', '50%')};

  align-items: center;
  display: flex;
  float: left;
  justify-content: center;
  position: relative;
`;

/**
 * `FooterLinksBlockWrapper` component.
 */

const FooterLinksBlockWrapper = styled.div`
  margin-top: ${units(5)};

  ${media.portrait.xxs`
    margin-top: 10px;
  `}
`;

/**
 * `ItemFooter` component.
 */

const ItemFooter = styled(ListItem)`
  ${({ display }) => `
    display: ${display};
    float: ${display === 'block' ? 'none' : 'left'};
    margin: ${display === 'block' ? '8px 0' : '0 16px'};
  `};
`;

/**
 * `MenuMobile` components.
 */

class MenuMobile extends Component {

  /**
   * Props.
   */

  props: Props;

  /**
   * Render menu links.
   */

  renderMenuLinks = (): React$Element<*> => {
    const { location, menuLinks } = this.props;

    return (
      <List>
        {menuLinks.map(({ key, title }: Object): React$Element<*> => {
          const isActive = isLocalizedUrlActive(key, location.pathname);

          return (
            <Item key={title}>
              <LinkItem
                className={branch(isActive, 'active')}
                onClick={() => { setBodyOverflow(false); }}
                to={getLocalizedUrl(key)}
              >
                {title}
              </LinkItem>
            </Item>
          );
        })}
      </List>
    );
  }

  /**
   * Render footer links.
   */

  renderFooterLinks = (display: string): React$Element<*> => {
    const { footerLinks } = this.props;
    const links = filter(footerLinks, ['isMobile', true]);

    return (
      <List>
        {links.map((link: Object): React$Element<*> => (
          <ItemFooter
            display={display}
            key={link.title}
          >
            <LinkDefault
              color={colors.shadesOfBlue.oxfordBlue}
              onClick={() => { setBodyOverflow(false); }}
              to={getLocalizedUrl(link.key)}
            >
              {link.title}
            </LinkDefault>
          </ItemFooter>
        ))}
      </List>
    );
  }

  /**
   * Render.
   */

  render(): React$Element<*> {
    const { isOpen } = this.props;

    return (
      <Panel active={isOpen}>
        <PanelContainer active={isOpen}>
          <Container>
            <Row>
              <Col
                xs={10}
                xsOffset={1}
              >
                <Layout textAlignXs={'center'}>
                  {this.renderMenuLinks()}
                </Layout>

                <MediaQuery maxWidth={breakpoints.xs - 1}>
                  <FooterLinksBlockWrapper>
                    {this.renderFooterLinks('block')}
                  </FooterLinksBlockWrapper>
                </MediaQuery>
              </Col>
            </Row>
          </Container>

          <FooterLinksInlineWrapper>
            <MediaQuery minWidth={breakpoints.xs}>
              {this.renderFooterLinks('block')}
            </MediaQuery>
          </FooterLinksInlineWrapper>

          <MediaQuery maxWidth={breakpoints.xs - 1}>
            <FooterNav active={isOpen}>
              <FooterNavLink>
                <Languages />
              </FooterNavLink>

              <FooterNavLink>
                <LoginButton />
              </FooterNavLink>
            </FooterNav>
          </MediaQuery>
        </PanelContainer>
      </Panel>
    );
  }

}

/**
 * Export `MenuMobile` components.
 */

export default withRouter(MenuMobile);



// WEBPACK FOOTER //
// ../src/client/components/navbar/menu-mobile.js